<template>
  <div class="unplanned-leave-table">
    <div v-if="isNew" class="row row-edit">
      <EditUnplannedLeaveRow
        :isNew="true"
        :leave="editing"
        :configuration="configuration"
        :month="month"
        @cancel="cancel"
        @save="save($event)"
      />
    </div>
    <template v-for="(row, index) in rowsInMonth">
      <div class="row" :class="{ 'row-edit': editing === row }" :key="index">
        <template v-if="editing !== row">
          <div class="row-data" data-testid="unplanned-leave-table-existing">
            <div>{{ formatDate(row.startDate) }}</div>
            <div>{{ formatDate(row.endDate) }}</div>
            <div>{{ formatPercentage(row.percentage) }}</div>
          </div>
          <div class="row-actions">
            <AlButton
              @click="edit(row)"
              v-if="!isNew"
              data-testid="unplanned-leave-table-edit-existing"
              id="unplanned-leave-table-edit-existing"
              :disabled="!editingAllowed"
              :tooltip="
                editingAllowed
                  ? ''
                  : 'New adjustments cannot be made to months in the past'
              "
              ><i class="fa fa-edit"
            /></AlButton>
            <AlButton
              @click="remove(row)"
              v-if="!isNew"
              variant="warning"
              data-testid="unplanned-leave-table-delete-existing"
              id="unplanned-leave-table-delete-existing"
              ><i class="fa fa-trash-alt"
            /></AlButton>
          </div>
        </template>
        <template v-if="editing === row">
          <EditUnplannedLeaveRow
            :isNew="false"
            :leave="editing"
            :configuration="configuration"
            :month="month"
            @cancel="cancel"
            @save="save($event, index)"
          />
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { format, isSameMonth, parseISO } from "date-fns";
import { Translator } from "@agyletime/wfm-vue-common";
import { Al2Button as AlButton } from "@playvox/almendra-components";
import EditUnplannedLeaveRow from "./EditUnplannedLeaveRow.vue";
import { utcToZonedTime } from "date-fns-tz";

export default {
  name: "UnplannedLeaveTable",
  mixins: [Translator],
  components: {
    EditUnplannedLeaveRow,
    AlButton,
  },
  props: {
    configuration: {
      type: Object,
      required: true,
    },
    rows: {
      type: Array,
      required: true,
    },
    month: {
      type: String,
      required: true,
    },
    isNew: {
      type: Boolean,
      default: false,
    },
    editingAllowed: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    isNew() {
      if (this.isNew) {
        this.editing = {};
      } else {
        this.editing = null;
      }
    },
    $route() {
      this.editing = null;
    },
  },
  data() {
    return {
      editing: null,
    };
  },
  computed: {
    rowsInMonth() {
      return this.rows
        .map((row, index) => ({
          ...row,
          index,
        }))
        .filter((row) => {
          const dateOne = utcToZonedTime(row.startDate, "UTC");
          const dateTwo = utcToZonedTime(new Date(this.month), "UTC");
          return isSameMonth(dateOne, dateTwo);
        });
    },
  },
  methods: {
    formatDate(value) {
      let dateToFormat = utcToZonedTime(value, "UTC");
      if (typeof dateToFormat === "string") {
        dateToFormat = parseISO(value);
      }
      return format(dateToFormat, "yyyy-MM-dd");
    },
    formatPercentage(value) {
      return `${value}%`;
    },
    edit(row) {
      this.editing = row;
    },
    remove(row) {
      const rows = [...this.rows];
      rows.splice(row.index, 1);
      this.$emit("save", rows);
    },
    cancel() {
      this.editing = null;
      this.$emit("cancel");
    },
    save(leave, index) {
      const rows = [...this.rows];
      if (this.isNew) {
        this.$emit("save", [leave, ...rows]);
      } else {
        rows.splice(index, 1, leave);
        this.$emit("save", rows);
      }
      this.editing = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.edit-unplanned-leave {
  max-width: 100%;
  width: 20rem;
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
}

.row {
  padding: 1rem;
  border-bottom: 1px solid var(--color-eerie-black-40);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;

  &:not(.row-edit):hover {
    background-color: var(--color-playvox-green-10);

    .row-actions > * {
      opacity: 1;
      pointer-events: all;
    }
  }
}

.row-data {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  flex: 1;

  & > * {
    width: 25%;
  }
}

.row-actions > * {
  margin-right: 0.5rem;
  opacity: 0;
  pointer-events: none;
}

@media (max-width: 899px) {
  .row-data {
    min-width: 100%;
  }
  .row-data > * {
    margin-bottom: 0.5rem;
  }
}

@media (max-width: 650px) {
  .row-data > * {
    min-width: 50%;
  }
  .row-actions > * {
    opacity: 1;
    pointer-events: all;
  }
}
</style>

<style>
.dr-input--sm.dr-input--has-icon .dr-input__append {
  padding-top: 3px;
}

.panel-header {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
}
</style>
